/**
 * /campaigns/:id/higgsfield — gallery of Higgsfield-generated clips.
 *
 * Surfaces the campaign's `source = 'higgsfield'` assets (issue #589) —
 * the MP4s produced out-of-band via @higgsfield/cli (issue #581) and
 * uploaded through the Assets tab. This is a read/playback view so the
 * admin can review the UGC reels for a campaign in one place instead of
 * scrolling the full asset library.
 *
 * Source filtering is client-side: the assets GET endpoint doesn't take
 * a `source` query param, and a campaign's library is small enough
 * (≤500) that filtering in-browser is instant — same approach the
 * Assets tab uses for its source chips.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../../lib/api';
import { useCampaignContext } from '../CampaignDetail';

interface Asset {
  id: string;
  campaign_id: string | null;
  type: string;
  source: string;
  tags: string[];
  description: string;
  filename: string | null;
  size_bytes: number | null;
  mime_type: string | null;
  created_at: string;
  public_url: string | null;
}

export function HiggsfieldTab() {
  const { campaign } = useCampaignContext();
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setAssets(null);
    const params = new URLSearchParams({
      campaign_id: campaign.id,
      scope: 'campaign',
      limit: '500',
    });
    apiJson<{ assets: Asset[] }>(`/api/marketing/assets?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setAssets(res.assets.filter((a) => a.source === 'higgsfield'));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load Higgsfield clips');
        setAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.id]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Higgsfield clips</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
          MP4s generated via <code>@higgsfield/cli</code> and uploaded to this campaign. Upload new
          clips from the{' '}
          <Link to={`/campaigns/${campaign.id}/assets`} style={{ color: 'var(--color-primary)' }}>
            Assets tab
          </Link>{' '}
          (set the source to higgsfield). Wire up a reusable persona on the{' '}
          <Link to="/characters" style={{ color: 'var(--color-primary)' }}>
            Characters
          </Link>{' '}
          page to keep them consistent.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {assets === null && (
        <div className="card" style={{ padding: 24 }}>
          <div className="spinner" />
        </div>
      )}

      {assets && assets.length === 0 && !error && (
        <div
          className="card"
          style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}
        >
          No Higgsfield clips for this campaign yet. Generate them with{' '}
          <code>@higgsfield/cli</code>, then upload via the Assets tab with the higgsfield source.
        </div>
      )}

      {assets && assets.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          {assets.map((asset) => (
            <ClipCard key={asset.id} asset={asset} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClipCard({ asset }: { asset: Asset }) {
  const isVideo = (asset.mime_type ?? '').startsWith('video/');
  return (
    <article className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
      {asset.public_url && isVideo ? (
        <video
          src={asset.public_url}
          controls
          playsInline
          muted
          style={{
            width: '100%',
            aspectRatio: '9 / 16',
            objectFit: 'cover',
            borderRadius: 8,
            background: '#000',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            aspectRatio: '9 / 16',
            background: 'var(--color-surface-alt)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-muted)',
            fontSize: 12,
          }}
        >
          {asset.public_url ? (
            <a href={asset.public_url} target="_blank" rel="noopener noreferrer">
              Open clip ↗
            </a>
          ) : (
            'Unavailable'
          )}
        </div>
      )}
      <strong style={{ fontSize: 13, wordBreak: 'break-word' }}>
        {asset.filename ?? asset.id.slice(0, 8)}
      </strong>
      {asset.description && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
          {asset.description}
        </p>
      )}
      {asset.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {asset.tags.map((t) => (
            <span
              key={t}
              style={{
                fontSize: 11,
                background: 'var(--color-surface-alt)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      )}
      <small style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
        {asset.size_bytes ? formatBytes(asset.size_bytes) : ''} ·{' '}
        {new Date(asset.created_at).toLocaleDateString()}
      </small>
    </article>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
